import { useRef, useState } from "react";
import { Overlay, OverlayTrigger, Popover, Tooltip } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { toast } from "react-toastify";
import { useStateMachine } from "little-state-machine";

import { exportQuoteRequest } from "api";
import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import { ReactComponent as ExportIcon } from "assets/images/iconsV2/share-01.svg";
import { ReactComponent as PDF } from "assets/images/iconsV2/pdf-topnav.svg";
import { ReactComponent as CSV } from "assets/images/iconsV2/csv.svg";
import { ReactComponent as XLSX } from "assets/images/iconsV2/xlsx.svg";
import testIds from "constants/testIds";
import errorToast from "helpers/errorToast";
import usePermissions from "hooks/usePermissions";
import MenuButton from "./MenuButton";

const height = "32";
const width = "32";

interface IExportList {
  title: string;
  testId: string;
  icon: JSX.Element;
  isEnabled?: boolean;
}

function Export() {
  const { state }: any = useStateMachine();
  const { permissions } = usePermissions();
  const { user_type } = JSON.parse(permissions || "{}");
  const [showPopover, setShowPopover] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const { data: shipmentData } = useGetShipmentBasic(state.shipmentId, {
    enabled: !!state.shipmentId,
    refetchOnMount: false,
  });

  const exportQuoteFileType = (exportType: string) => {

    let exportUrl = "";
    let fileName = "";
    let mimeType = "";

    switch (exportType) {
      case "Request to CSV":
        exportUrl = "export-to-csv";
        fileName = `${state.shipmentId}.csv`;
        mimeType = "text/csv";
        break;
      case "Request to PDF":
        exportUrl = "export-to-pdf";
        fileName = `${state.shipmentId}.pdf`;
        mimeType = "application/pdf";
        break;
      case "Quotes to XLSX":
        exportUrl = "quotes/export-to-xlsx";
        fileName = `${state.shipmentId}-quotes.xlsx`;
        mimeType = "application/vnd.ms-excel";
        break;
      case "Quotes to PDF":
        exportUrl = "quotes/export-to-pdf";
        fileName = `${state.shipmentId}-quotes.pdf`;
        mimeType = "application/pdf";
        break;
      case "Shipment to PDF":
        exportUrl = "print-shipment-details";
        fileName = `${state.shipmentId}.pdf`;
        mimeType = "application/pdf";
        break;
      default:
        return;
    }

    exportQuoteRequest(state.shipmentId, exportUrl)
      .then((res) => {
        toast("Export successful", { type: "success" });
        const blob = new Blob([res.data], { type: mimeType });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute("download", fileName);
        link.click();
      })
      .catch((err) => errorToast(err, "Could not export file. Please try again later."));
  };

  const exportList: IExportList[] = [
    {
      title: "Request to CSV",
      testId: testIds.SHIPMENT_DASHBOARD_EXPORT_REQUEST_CSV_BUTTON,
      icon: <CSV width={width} height={height} />,
      isEnabled: true,
    },
    {
      title: "Request to PDF",
      testId: testIds.SHIPMENT_DASHBOARD_EXPORT_REQUEST_PDF_BUTTON,
      icon: <PDF width={width} height={height} />,
      isEnabled: true,
    },
    {
      title: "Quotes to XLSX",
      testId: testIds.SHIPMENT_DASHBOARD_EXPORT_QUOTE_XLSX_BUTTON,
      icon: <XLSX width={width} height={height} />,
      isEnabled: state.quotationsAvailable,
    },
    {
      title: "Quotes to PDF",
      testId: testIds.SHIPMENT_DASHBOARD_EXPORT_QUOTE_PDF_BUTTON,
      icon: <PDF width={width} height={height} />,
      isEnabled: state.quotationsAvailable,
    },
    {
      title: "Shipment to PDF",
      testId: testIds.SHIPMENT_DASHBOARD_EXPORT_SHIPMENT_PDF_BUTTON,
      icon: <PDF width={width} height={height} />,
      isEnabled: shipmentData?.shipment?.shipment_status, // No status means shipment is not yet booked and cannot be exported
    },
  ];

  function getExportType(exportItem: IExportList) {
    if (user_type === "provider") {
      return exportItem.title !== "Quotes to XLSX";
    }
    return exportItem;
  }

  const popover = (
    <Popover id="v2-more-actions-popover" className="v2-more-actions-popover">
      <Popover.Content style={{ padding: 0 }} data-testid={testIds.TOPNAV_V2_EXPORT_MENU}>
        <div className="d-flex flex-column" data-testid={testIds.TOPNAV_V2_EXPORT_MENU_LIST}>
          {exportList.filter(getExportType).map(({ title, testId, icon, isEnabled }) => (
            <MenuButton
              key={title}
              onClick={() => {
                if (!isEnabled) return;
                exportQuoteFileType(title);
                setShowPopover(false);
              }}
              testId={testId}
              icon={icon}
              title={title}
              isEnabled={isEnabled}
            />
          ))}
        </div>
      </Popover.Content>
    </Popover>
  );

  return (
    <div data-testid={testIds.TOPNAV_V2_EXPORT_CONTAINER} style={{ position: "relative" }}>
      <OverlayTrigger
        placement="bottom"
        delay={{ show: 250, hide: 0 }}
        overlay={
          <Tooltip id={testIds.TOPNAV_V2_EXPORT_TOOLTIP}>
            <FormattedMessage id="components.molecules.navigation.export" defaultMessage="Export" />
          </Tooltip>
        }
      >
        <button
          ref={buttonRef}
          type="button"
          className="topnav-v2__action-btn"
          aria-label="Export"
          data-testid={testIds.SHIPMENT_DASHBOARD_EXPORT_BUTTON}
          onClick={() => setShowPopover((prev) => !prev)}
        >
          <ExportIcon />
        </button>
      </OverlayTrigger>
      <Overlay
        target={buttonRef.current}
        show={showPopover}
        placement="bottom-end"
        rootClose
        onHide={() => setShowPopover(false)}
      >
        {popover}
      </Overlay>
    </div>
  );
}

export default Export;
